import { useQuery } from '@apollo/client';
import React, { forwardRef, useImperativeHandle } from 'react';
import { FetchTaskListQuery, TaskStatus, TaskType, UserRole } from '@css-xp/apd-common';
import { ErrorComponents } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { REQUEST_TYPE } from '../../constants/filters';
import { CLOSED_TASKS_TABLE_ROWS } from '../../constants/table';
import { OPEN_TASK_STATUS } from '../../constants/task';
import { fetchTaskList } from '../../queries/tasks';
import { TaskListTable } from '../task-list-table/task-list-table';
import { createTableHead, createTableRows } from './my-tasks-list-utils';

interface MyTaskListProps {
  refetchAll?: () => void;
}

const MyTasksList = forwardRef(({ refetchAll }: MyTaskListProps, ref) => {
  const user = useApplicationContext();
  const { data, loading, error, refetch } = useQuery<FetchTaskListQuery>(fetchTaskList, {
    skip: !user?.userId,
    variables: {
      staffId: user?.userId?.split('@')?.[0],
      taskType: TaskType.MyTasks,
      taskStatus: TaskStatus.Open,
    },
  });

  const userRole = React.useMemo(() => {
    return data?.taskList?.userRole;
  }, [data]);

  const head = React.useMemo(() => createTableHead(true), []);

  const visibleRows = React.useMemo(() => {
    const taskslist = data?.taskList?.tasks ?? [];
    const tableRowsParam = {
      taskslist,
      userRole,
      refetch,
      refetchAll,
    };
    return createTableRows(tableRowsParam);
  }, [data?.taskList?.tasks, refetchAll, refetch, userRole]);

  useImperativeHandle(ref, () => ({
    reload: refetch,
  }));

  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  return (
    <TaskListTable
      rows={visibleRows}
      head={head}
      rowsPerPage={userRole && userRole === UserRole.ADVOCATE ? CLOSED_TASKS_TABLE_ROWS : undefined}
      headingText="My requests"
      isLoading={loading}
      requestTypeFilterOptions={REQUEST_TYPE}
      statusFilterOptions={OPEN_TASK_STATUS}
    />
  );
});

export default MyTasksList;
